import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users as UsersIcon, FileSpreadsheet, Wallet, History, Plus,
  PanelLeftClose, PanelLeftOpen, LogOut, Shield, UserCog, CheckSquare,
  Bell, MessageCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { useChat } from '@/contexts/ChatContext';
import { log } from '@/lib/log';

const STORAGE_KEY = 'ral.sidebarCollapsed';

// Every sidebar quick-add ("+") declared in one place.
const QUICK_ADD = {
  Clients: { href: '/clients/new', testid: 'nav-add-client', title: 'Add new client' },
  Finance: { href: '/finance/add', testid: 'nav-add-statement', title: 'Add statement' },
  Task: { href: '/tasks/new', testid: 'nav-add-task', title: 'Assign new task' },
};


const fmtAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const NOTIF_ICONS = {
  task_new:      '🆕',
  task_renotify: '🔁',
  task_done:     '✅',
};

const AppShell = ({ children }) => {
  const location = useLocation();
  const { user, logout, has } = useAuth();
  const { unread: notifUnread, hasNew, pendingCount, notifications, markAllRead, requestPushPermission } = useNotifications();
  const { totalUnread: chatUnread, openPanel: openChatPanel } = useChat();
  const [notifOpen, setNotifOpen] = useState(false);
  const notifPopRef = useRef(null);

  // Ask for push permission after first user interaction.
  useEffect(() => {
    const onAnyClick = () => {
      requestPushPermission();
      window.removeEventListener('click', onAnyClick);
    };
    window.addEventListener('click', onAnyClick);
    return () => window.removeEventListener('click', onAnyClick);
  }, [requestPushPermission]);

  // Close notif popover on outside click
  useEffect(() => {
    if (!notifOpen) return undefined;
    const onDoc = (e) => {
      if (notifPopRef.current && !notifPopRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [notifOpen]);

  const handleOpenNotifs = () => {
    setNotifOpen((o) => {
      const next = !o;
      if (next && notifUnread > 0) markAllRead();
      return next;
    });
  };
  // Sidebar collapsed-state preference. This is a UI-only boolean ('0' | '1'),
  // not an auth token or PII — safe to persist in localStorage.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; }
    catch (err) { log.debug('[appshell] localStorage read failed', err?.message || err); return false; }
  });

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); }
      catch (err) { log.debug('[appshell] localStorage write failed', err?.message || err); }
      return next;
    });
  }, []);

  // Hotkey: Cmd/Ctrl + B to toggle (familiar IDE pattern)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  const allNav = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, feature: 'dashboard', match: (p) => p === '/' },
    { name: 'Clients',   href: '/clients',  icon: UsersIcon,        feature: 'clients',   match: (p) => p.startsWith('/clients') },
    { name: 'Finance',   href: '/finance',  icon: Wallet,           feature: 'finance',   match: (p) => p.startsWith('/finance') },
    { name: 'Templates', href: '/templates', icon: FileSpreadsheet, feature: 'templates', match: (p) => p === '/templates' },
    { name: 'History',   href: '/history',  icon: History,          feature: 'history',   match: (p) => p === '/history' },
  ];
  const navigation = allNav.filter((item) => has(item.feature));
  const isSuperAdmin = user?.role === 'super_admin';

  const width = collapsed ? 'w-20' : 'w-64';

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="flex h-screen bg-surface">
      {/* Sidebar */}
      <div
        data-testid="sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
        className={`relative ${width} bg-white border-r border-mute/40 flex flex-col transition-[width] duration-300 ease-out`}
      >
        {/* Floating collapse toggle on the right edge */}
        <button
          data-testid="sidebar-toggle"
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand (⌘B)' : 'Collapse (⌘B)'}
          className="absolute -right-3 top-8 z-20 h-7 w-7 grid place-items-center rounded-full bg-white border border-mute/60 text-ink/70 shadow-sm hover:bg-ink hover:text-white hover:border-ink transition-all"
        >
          {collapsed ? <PanelLeftOpen className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-3.5 h-3.5" />}
        </button>

        <div className={`pt-6 pb-5 ${collapsed ? 'px-4' : 'px-6'} flex items-center justify-center gap-3`}>
          <img
            src="/logo.png"
            alt="Ralicense.in"
            className={`${collapsed ? 'h-10' : 'h-[60px]'} w-auto object-contain transition-all duration-300`}
            data-testid="brand-logo"
          />
        </div>

        <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-3'} py-2 space-y-1.5`}>
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = item.match(location.pathname);
            const quickAdd = QUICK_ADD[item.name] || null;
            return (
              <div key={item.name} className="flex items-center gap-1.5">
                <Link
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  title={collapsed ? item.name : undefined}
                  className={`flex-1 flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-3'} rounded-2xl text-base font-semibold transition-all ${
                    active
                      ? 'bg-ink text-white shadow-sm'
                      : 'text-ink/70 hover:bg-surface-100 hover:text-ink'
                  }`}
                >
                  <Icon className={`w-6 h-6 ${collapsed ? '' : 'mr-3'} ${active ? 'text-white' : 'text-ink/60'}`} />
                  {!collapsed && item.name}
                </Link>
                {quickAdd && !collapsed && (
                  <Link
                    to={quickAdd.href}
                    data-testid={quickAdd.testid}
                    title={quickAdd.title}
                    className="p-2 rounded-full bg-primary text-white hover:bg-primary-hover transition-colors shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            );
          })}

          {/* Tasks — always-on for every logged-in user */}
          {user && (
            <div className="flex items-center gap-1.5">
              <Link
                to="/tasks"
                data-testid="nav-tasks"
                title={collapsed ? 'Task' : undefined}
                className={`flex-1 relative flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-3'} rounded-2xl text-base font-semibold transition-all ${
                  location.pathname.startsWith('/tasks')
                    ? 'bg-ink text-white shadow-sm'
                    : 'text-ink/70 hover:bg-surface-100 hover:text-ink'
                }`}
              >
                <CheckSquare className={`w-6 h-6 ${collapsed ? '' : 'mr-3'} ${location.pathname.startsWith('/tasks') ? 'text-white' : 'text-ink/60'}`} />
                {!collapsed && 'Task'}
                {/* Blinking red dot when there is something to action */}
                {(hasNew || pendingCount > 0) && (
                  <span
                    data-testid="task-dot"
                    className={`absolute ${collapsed ? 'top-2 right-2' : 'top-3 right-3'} w-2.5 h-2.5 rounded-full bg-red-500 ${hasNew ? 'animate-ping-soft' : ''}`}
                    aria-label="You have pending tasks"
                  />
                )}
              </Link>
              {!collapsed && (user.role === 'super_admin' || user.can_assign_tasks) && (
                <Link
                  to={QUICK_ADD.Task.href}
                  data-testid={QUICK_ADD.Task.testid}
                  title={QUICK_ADD.Task.title}
                  className="p-2 rounded-full bg-primary text-white hover:bg-primary-hover transition-colors shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          )}

          {/* Messages — opens floating chat panel */}
          {user && (
            <button
              type="button"
              onClick={() => openChatPanel()}
              data-testid="nav-messages"
              title={collapsed ? 'Messages' : undefined}
              className={`relative w-full flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-3'} rounded-2xl text-base font-semibold transition-all text-ink/70 hover:bg-surface-100 hover:text-ink`}
            >
              <MessageCircle className={`w-6 h-6 ${collapsed ? '' : 'mr-3'} text-ink/60`} />
              {!collapsed && <span className="flex-1 text-left">Messages</span>}
              {chatUnread > 0 && (
                <span
                  data-testid="nav-messages-badge"
                  className={`${collapsed ? 'absolute top-2 right-2' : ''} min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center`}
                >
                  {chatUnread > 99 ? '99+' : chatUnread}
                </span>
              )}
            </button>
          )}

          {/* Notifications — opens inline popover */}
          {user && (
            <div className="relative" ref={notifPopRef}>
              <button
                type="button"
                onClick={handleOpenNotifs}
                data-testid="nav-notifications"
                title={collapsed ? 'Notifications' : undefined}
                className={`relative w-full flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-3'} rounded-2xl text-base font-semibold transition-all ${notifOpen ? 'bg-surface-100 text-ink' : 'text-ink/70 hover:bg-surface-100 hover:text-ink'}`}
              >
                <Bell className={`w-6 h-6 ${collapsed ? '' : 'mr-3'} text-ink/60`} />
                {!collapsed && <span className="flex-1 text-left">Notifications</span>}
                {notifUnread > 0 && (
                  <span
                    data-testid="nav-notifications-badge"
                    className={`${collapsed ? 'absolute top-2 right-2' : ''} min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center`}
                  >
                    {notifUnread > 99 ? '99+' : notifUnread}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div
                  data-testid="notification-drawer"
                  className="absolute left-full top-0 ml-3 w-96 max-h-[70vh] overflow-y-auto bg-white rounded-2xl border border-mute/40 shadow-2xl z-[120]"
                >
                  <div className="px-5 py-3 border-b border-mute/30 flex items-center justify-between">
                    <div className="font-display text-base font-bold text-ink tracking-tight">Notifications</div>
                    <Link
                      to="/tasks"
                      onClick={() => setNotifOpen(false)}
                      className="text-xs text-primary font-semibold hover:underline"
                    >
                      View all
                    </Link>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="px-5 py-12 text-center text-sm text-ink/50">No notifications yet.</div>
                  ) : (
                    <ul className="divide-y divide-mute/20">
                      {notifications.slice(0, 20).map((n) => (
                        <li key={n.id} className={`px-5 py-3 flex items-start gap-3 ${n.read ? 'bg-white' : 'bg-blue-50/40'}`}>
                          <span className="text-base shrink-0 mt-0.5">{NOTIF_ICONS[n.type] || '🔔'}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-ink truncate">{n.message}</div>
                            <div className="text-[11px] text-ink/40 mt-0.5">{fmtAgo(n.created_at)}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Users & Access — super_admin only */}
          {isSuperAdmin && (
            <Link
              to="/users"
              data-testid="nav-users"
              title={collapsed ? 'Users & Access' : undefined}
              className={`flex items-center ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-3'} rounded-2xl text-base font-semibold transition-all ${
                location.pathname.startsWith('/users')
                  ? 'bg-ink text-white shadow-sm'
                  : 'text-ink/70 hover:bg-surface-100 hover:text-ink'
              }`}
            >
              <UserCog className={`w-6 h-6 ${collapsed ? '' : 'mr-3'} ${location.pathname.startsWith('/users') ? 'text-white' : 'text-ink/60'}`} />
              {!collapsed && 'Users & Access'}
            </Link>
          )}
        </nav>

        {/* Footer: user identity + logout */}
        {user && (
          <div className={`border-t border-mute/30 ${collapsed ? 'px-2 py-3' : 'px-3 py-4'} space-y-2`}>
            {!collapsed && (
              <div className="px-3 pb-2 flex items-center gap-2 text-xs">
                {isSuperAdmin
                  ? <Shield className="w-3.5 h-3.5 text-primary shrink-0" />
                  : <UsersIcon className="w-3.5 h-3.5 text-ink/50 shrink-0" />}
                <div className="min-w-0">
                  <div className="font-semibold text-ink truncate" data-testid="current-user-name">{user.name}</div>
                  <div className="text-ink/50 truncate">{user.email}</div>
                </div>
              </div>
            )}
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              title={collapsed ? 'Sign out' : undefined}
              className={`w-full flex items-center ${collapsed ? 'justify-center px-0 py-2.5' : 'px-4 py-2.5'} rounded-2xl text-sm font-semibold text-ink/70 hover:bg-red-50 hover:text-red-600 transition-colors`}
            >
              <LogOut className={`w-4 h-4 ${collapsed ? '' : 'mr-2'}`} />
              {!collapsed && 'Sign out'}
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
